import React, { useEffect, useState } from "react";
import { Button, Card, CardContent, List, Typography, CircularProgress, Box } from "@mui/material";
import { CreateInitialEvaluationDto, GetInitialEvaluationDto } from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import InitialEvaluationCard from "./InitialEvaluationCard";
import notificationStore from "../../../stores/Notification/notificationStore";

interface InitialEvaluationListProps {
  patientId: number;
  limit: number;
}

const InitialEvaluationList = ({
  patientId,
  limit,
}: InitialEvaluationListProps) => {
  const { clinicalDocumentation } = useApi();

  const [initialEvaluations, setInitialEvaluations] = useState<GetInitialEvaluationDto[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (patientId) {
      // Reset state when patient changes
      setInitialEvaluations([]);
      setPage(1);
      setHasMore(true);
      loadItems(1);
    }
  }, [patientId]);

  const handleCreateInitialEvaluation = async (initialEvaluation: CreateInitialEvaluationDto) => {
    try {
      const data = await clinicalDocumentation.createClinicalInitialEvaluation(initialEvaluation);
      setInitialEvaluations([data, ...initialEvaluations]);
    } catch (error: any) {
      // console.log("Error creating initial evaluation", error);
      notificationStore.showMessage(error?.message, 'error', error);
    }
  };

  const handleUpdateInitialEvaluation = async (id: number, initialEvaluation: CreateInitialEvaluationDto) => {
    try {
      const data = await clinicalDocumentation.updateClinicalInitialEvaluation(id, initialEvaluation);
      const index = initialEvaluations.findIndex((ie) => ie.id === id);
      if (index > -1) {
        const updated = [...initialEvaluations];
        updated[index] = data;
        setInitialEvaluations(updated);
      }
    } catch (error: any) {
      // console.log("Error updating initial evaluation", error);
      notificationStore.showMessage(error?.message, 'error', error);
    }
  };

  const loadItems = async (pageToLoad?: number) => {
    const currentPage = pageToLoad ?? page;
    try {
      setLoading(true);
      const data = await clinicalDocumentation.getClinicalInitialEvaluation(patientId, { params: { limit, page: currentPage } } as any);
      if (data && data.length) {
        setInitialEvaluations((prev) => pageToLoad === 1 ? data : [...prev, ...data]);
        setPage(currentPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      // console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <List>
        {initialEvaluations &&
          initialEvaluations.map((initialEvaluation) => (
            <InitialEvaluationCard
              patientId={patientId}
              key={initialEvaluation.id}
              initialEvaluation={initialEvaluation}
              onCreateInitialEvaluation={handleCreateInitialEvaluation}
              onUpdateInitialEvaluation={handleUpdateInitialEvaluation}
            />
          ))}
      </List>
      {hasMore && (
        <Box display="flex" justifyContent="center" alignItems="center" marginTop={2}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Button variant="contained" onClick={() => loadItems()}>
              Load More
            </Button>
          )}
        </Box>
      )}
      {!hasMore && initialEvaluations.length === 0 && (
        <Typography variant="body1" align="center">
          Keine Erstbefunde vorhanden.
        </Typography>
      )}
    </Box>
  );
};

export default InitialEvaluationList;
